import React, {useEffect, useState} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {useLocation, useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';

import emptyKnewnew from '@/products/user_default.svg';
import {isOpenMyPageModal} from '@/store';
import {isMobile} from '@/utils';
import useLogger, {Action} from '@/hooks/useLogger';
import {UserInfo, user} from '@/store/user';
import {getCouponList} from '@/services/coupon';
import useAuth from '@/hooks/useAuth';
import {getBanner} from '@/services/user';

import AuthModal from '../common/AuthModal';
import styles from './styles.module.scss';

interface Props {
  isOther: boolean;
  profile: UserInfoObj | undefined;
}

export default function MyInfo({isOther, profile}: Props) {
  const navigator = useNavigate();
  const location = useLocation();
  const {openAuth} = useAuth();
  const setIsOpenModal = useSetRecoilState(isOpenMyPageModal);
  const userInfo = useRecoilValue(UserInfo);
  const accessToken = useRecoilValue(user);
  const [errorCnt, setErrorCnt] = useState(0);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isOpenAuthModal, setIsOpenAuthModal] = useState(false);
  const [hasNewCoupon, setHasNewCoupon] = useState(false);
  const [isClickedAuth, setIsClickedAuth] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const ADMIN_COUPON_LIST_STORAGE_KEY = 'checked_coupon_list';
  const sender = useLogger();
  const messages = [
    <>
      본인인증에 실패했습니다
      <br />
      다시 시도해 주세요
    </>,
    <>
      1일 인증 가능 횟수를 초과했습니다. <br />
      <span>24시간</span> 이후에 다시 시도해 주세요.
    </>,
    <>
      이미 다른 계정으로
      <br />
      본인 인증이 완료되었습니다.
    </>,
  ];
  const isActiveSNS = (type: string) => {
    return (
      profile &&
      profile.snsInfo &&
      Object.keys(profile.snsInfo).length > 0 &&
      profile.snsInfo[type as keyof typeof profile.snsInfo]?.length > 0
    );
  };
  const moveToLink = (sns: string) => {
    const link = profile?.snsInfo[sns as keyof typeof profile.snsInfo];
    if (link && link.length > 0) {
      window.open(link.startsWith('http') ? link : `https://${link}`, '_blank');
    }
  };

  const getCouponData = async () => {
    const res = await getCouponList(accessToken, 0);
    const _coupons = res.data.filter(c => {
      return dayjs().diff(c.expireDate) < 0;
    });
    const existedAdminCoupons = JSON.parse(
      localStorage.getItem(ADMIN_COUPON_LIST_STORAGE_KEY) || '[]',
    );
    const adminCoupons = _coupons.filter(c => c.target !== 'code');
    adminCoupons.forEach(c => {
      if (existedAdminCoupons.indexOf(c.id) === -1) {
        setHasNewCoupon(true);
      }
    });
    setCoupons(_coupons);
  };

  const getBanners = async () => {
    const res = await getBanner(accessToken);
    setBanners(res.data);
  };

  useEffect(() => {
    const result = location.state?.certResult;
    if (result) {
      switch (result) {
        case 'duplicated':
          setErrorCnt(2);
          setIsOpenAuthModal(true);
          break;
        case 'failed':
          setErrorCnt(0);
          setIsOpenAuthModal(true);
          break;
        default:
          break;
      }
      setIsClickedAuth(false);
    }
  }, [location.state]);

  useEffect(() => {
    getCouponData();
    getBanners();
  }, []);

  return (
    <div className="my-info-wrapper">
      <div>
        <div className="profile">
          <div
            className="profile-image-wrapper"
            style={{
              background: `url(${
                profile?.profileImage ? profile.profileImage : emptyKnewnew
              }) center no-repeat`,
              width: '60px',
              height: '60px',
            }}></div>

          {!isOther && (
            <div
              className="options"
              onClick={() => {
                setIsOpenModal(true);
              }}></div>
          )}
        </div>
        <div className="profile-name-sns-link">
          <div className="profile-name">
            <button
              className="link-to-curator"
              onClick={() => navigator(`/curators/${profile?.id}`)}
              style={{
                display:
                  profile?.userType === 'curator' ? 'inline-flex' : 'none',
              }}>
              큐레이터
              <span className="icon arrow-right-main-pink-icon">&nbsp;</span>
            </button>
            <span
              className="creator"
              style={{
                display:
                  profile?.userType === 'creator' ? 'inline-block' : 'none',
              }}>
              크리에이터
            </span>{' '}
            <span className="name">
              {profile ? profile.nickname.slice(0, 15) : 'no name'}
            </span>
          </div>
          <div className="edit-user">
            <div className="sns-wrapper">
              <span
                className={`sns-icon youtube ${
                  isActiveSNS('youtube') ? 'active' : 'inactive'
                }`}
                onClick={() => moveToLink('youtube')}>
                &nbsp;
              </span>
              <span
                className={`sns-icon instagram ${
                  isActiveSNS('instagram') ? 'active' : 'inactive'
                }`}
                onClick={() => moveToLink('instagram')}>
                &nbsp;
              </span>
              <span
                className={`sns-icon naver-blog ${
                  isActiveSNS('naver') ? 'active' : 'inactive'
                }`}
                onClick={() => moveToLink('naver')}>
                &nbsp;
              </span>
            </div>
          </div>
        </div>
        <div className="introduce">{profile ? profile.headline : ''}</div>
        <div
          className={`button-wrapper ${isMobile() ? 'pc' : ''} ${
            styles.menuLinks
          }`}
          style={{
            display: isOther ? 'none' : 'flex',
            justifyContent: 'space-around',
          }}>
          <div>
            <button onClick={() => navigator('/order/list')}>
              <strong>구매내역</strong>
              <strong className={styles.move_to_arraow_icon}>
                <span className={styles.arrow_icon}>&nbsp;</span>
              </strong>
            </button>
            <button onClick={() => navigator('/user/coupon')}>
              <strong
                className={`${styles.coupon} ${
                  userInfo.isIdentified && !hasNewCoupon ? '' : styles.no_auth
                }`}>
                쿠폰
              </strong>
              <strong
                className={`${styles.move_to_arraow_icon} ${
                  userInfo.isIdentified ? '' : styles.no_auth
                }`}>
                <span
                  className={`${styles.couponCnt} ${
                    userInfo.isIdentified && coupons.length > 0
                      ? styles.pink
                      : ''
                  }`}>
                  {userInfo.isIdentified ? coupons.length : 0}장
                </span>
                <span className={styles.arrow_icon}>&nbsp;</span>
              </strong>
            </button>
          </div>
          <div>
            <button
              onClick={() => {
                sender({
                  _msg: 'mypage_likecollection_click',
                  _screen: window.location.pathname,
                  _action: Action.CLICK,
                });
                navigator('/my-collection');
              }}>
              <strong>찜한 컬렉션</strong>
              <strong className={styles.move_to_arraow_icon}>
                <span className={styles.arrow_icon}>&nbsp;</span>
              </strong>
            </button>
            <button onClick={() => navigator('/bookmark')}>
              <strong>담은 매거진</strong>
              <strong className={styles.move_to_arraow_icon}>
                <span className={styles.arrow_icon}>&nbsp;</span>
              </strong>
            </button>
          </div>
        </div>
        {!isOther && (
          <div
            className={styles.coupon_banner}
            onClick={() => {
              if (!isClickedAuth) {
                openAuth(location.pathname);
                setIsClickedAuth(true);
                setTimeout(() => {
                  setIsClickedAuth(false);
                }, 3000);
              }
            }}
            style={{display: userInfo.isIdentified ? 'none' : 'block'}}></div>
        )}
        {!isOther && banners.length > 0 && (
          <div
            className={styles.coupon_banner}
            onClick={() => navigator(banners[0].link)}
            style={{
              display: userInfo.isIdentified ? 'block' : 'none',
              background: `url('${banners[0].image}') no-repeat center / cover`,
            }}></div>
        )}
      </div>
      <AuthModal
        txt={messages[errorCnt]}
        isOpen={isOpenAuthModal}
        setIsOpen={setIsOpenAuthModal}
        btnType={'error'}
        action={() => setIsOpenAuthModal(false)}
      />
    </div>
  );
}
